import { FINANCIAL_REALTIME_TABLES, FINANCIAL_REFETCH_INTERVAL_MS } from './finance';

interface FinancialChannel {
  on(kind: 'postgres_changes', filter: { event: '*'; schema: 'public'; table: string; filter?: string }, callback: () => void): unknown;
  subscribe(callback: (status: string) => void): unknown;
}

/** Realtime is an invalidation hint. Independent polling and foreground reads
 * always fetch the authoritative server snapshot, including after reconnection. */
export function startFinancialRefresh<Channel extends FinancialChannel>(
  client: { channel(name: string): Channel; removeChannel(channel: Channel): Promise<unknown> },
  name: string,
  refresh: () => unknown | Promise<unknown>,
  options: { riderId?: string; extraTables?: readonly string[]; onForeground?: (callback: () => void) => () => void } = {},
) {
  let disposed = false;
  let refreshing = false;
  let again = false;
  const refetch = async () => {
    if (disposed) return;
    if (refreshing) { again = true; return; }
    refreshing = true;
    try { await refresh(); }
    catch { /* The query/UI owns errors; the independent fallback will retry. */ }
    finally {
      refreshing = false;
      if (again && !disposed) { again = false; void refetch(); }
    }
  };
  const poll = setInterval(() => void refetch(), FINANCIAL_REFETCH_INTERVAL_MS);
  let stopForeground: (() => void) | undefined;
  let channel: Channel | null = null;
  try { stopForeground = options.onForeground?.(() => void refetch()); } catch { /* Polling still works. */ }
  try {
    channel = client.channel(name);
    for (const table of new Set<string>([...FINANCIAL_REALTIME_TABLES, ...(options.extraTables ?? [])])) {
      channel.on('postgres_changes', { event: '*', schema: 'public', table,
        ...(options.riderId && table !== 'remittance_allocations' && table !== 'orders' ? { filter: `rider_id=eq.${options.riderId}` } : {}),
      }, () => void refetch());
    }
    channel.subscribe((status) => { if (status === 'SUBSCRIBED') void refetch(); });
  } catch { /* Missing/unavailable realtime must never disable the independent poll. */ }
  return () => {
    disposed = true; clearInterval(poll); stopForeground?.();
    if (channel) void client.removeChannel(channel).catch(() => undefined);
  };
}
